/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip } from 'uikit/tooltips';

import Button from './button';

import styles from '../styles/uikitButtonsViewToggle.module.scss';

const ViewToggle = ({
  buttons = [],
  className = '',
  selectedValue = '',
  testId = '',
  ...other
}) => {
  if (!buttons?.length) return null;

  const initialValidButtons = buttons.reduce((acc, button) => {
    const { icon, text } = button;
    if (!icon && !text) {
      console.warn('ViewToggle: Button not rendered. Must have text or icon');
      return acc;
    }
    acc.push(button);
    return acc;
  }, []);

  let validButtons = [];
  const isAllIcons = initialValidButtons.every((button) => button.icon);
  if (isAllIcons) {
    validButtons = initialValidButtons;
  } else {
    validButtons = initialValidButtons.filter((button) => button.text);
  }

  if (validButtons.length < 2) {
    console.warn('ViewToggle: Must have at least two valid buttons');
    return null;
  }

  const isOneSelectedButton = () => {
    const isSelected = (button) =>
      (selectedValue && button.key === selectedValue) || button.isSelected;
    const selectedButtons = validButtons.filter((button) => isSelected(button));
    return selectedButtons.length === 1;
  };
  if (!isOneSelectedButton()) {
    console.warn('ViewToggle: Must have exactly one selected button');
    return null;
  }
  const buttonComponents = validButtons.map((button) => {
    const { ariaLabel, icon, isSelected, key, text, tooltip, ...buttonProps } = button;
    const buttonIcon = isAllIcons && icon ? icon : '';
    const buttonText = buttonIcon ? '' : text;
    const isValidTooltip = tooltip && tooltip.content;

    const getAriaLabel = () => {
      if (buttonIcon) {
        return ariaLabel || text;
      }
      return '';
    };

    const buttonComponent = (
      <Button
        {...buttonProps}
        ariaLabel={getAriaLabel()}
        icon={buttonIcon}
        isSelected={selectedValue ? selectedValue === button.key : isSelected}
        key={!isValidTooltip && key}
        size="small"
        text={buttonText}
        variant={buttonIcon ? 'toggleIcon' : 'toggleText'}
      />
    );

    if (isValidTooltip) {
      return (
        <Tooltip {...tooltip} key={key}>
          {buttonComponent}
        </Tooltip>
      );
    }
    return buttonComponent;
  });

  return (
    <div
      className={classnames(styles.element, className)}
      role="group"
      data-testid={testId}
      {...other}
    >
      {buttonComponents}
    </div>
  );
};

ViewToggle.propTypes = {
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      ariaControls: PropTypes.string,
      ariaExpanded: PropTypes.bool,
      ariaLabel: PropTypes.string,
      className: PropTypes.string,
      icon: PropTypes.element,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      isDisabled: PropTypes.bool,
      isSelected: PropTypes.bool,
      key: PropTypes.string,
      onClick: PropTypes.func,
      testId: PropTypes.string,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      to: PropTypes.string,
      tooltip: PropTypes.shape({
        className: PropTypes.string,
        content: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func])
          .isRequired,
        delay: PropTypes.arrayOf(PropTypes.number),
        duration: PropTypes.arrayOf(PropTypes.number),
        placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
        zIndex: PropTypes.number,
      }),
    }),
  ),
  className: PropTypes.string,
  selectedValue: PropTypes.string,
  testId: PropTypes.string,
};

export { ViewToggle as ViewToggleStorybook };
export default React.memo(ViewToggle);
